"use client";

/**
 * MailMock — animated mock of the askFinz Mail + Calendar workspace for
 * /apps/mail.
 *
 * Pure presentation. Layout:
 *   - Tab strip at the top toggles between Mail and Calendar views.
 *   - Mail view: 3-pane layout (folders, threads, reading-pane with agent
 *     draft).
 *   - Calendar view: week grid with colour-coded events; one event is the
 *     Mailer-drafted auto-accept block highlighted with the brand accent.
 *
 * Animation loop:
 *   1. Mail view; Mailer drafts a reply to a partnership thread.
 *   2. A new meeting invite arrives in the inbox.
 *   3. Mailer drafts an "Accept + add to calendar" reply.
 *   4. View flips to Calendar; the accepted block slides into the grid.
 *   5. Loop resets.
 *
 * NOT the real Mail UI (`mail.askfinz.ai/components/mail-app.tsx`). Lives
 * in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Inbox,
  Filter,
  PenLine,
  Send,
  Star,
  Archive,
  Trash2,
  Bot,
  Sparkles,
  Paperclip,
  Reply,
  CornerUpLeft,
  CalendarDays,
  Mail as MailIcon,
  Clock,
  CheckCircle2,
} from "lucide-react";

type Folder = {
  id: string;
  label: string;
  icon: typeof Inbox;
  count?: number;
  active?: boolean;
};

const FOLDERS: Folder[] = [
  { id: "inbox",   label: "Inbox",   icon: Inbox,    count: 8,  active: true },
  { id: "triage",  label: "Triage",  icon: Filter,   count: 12 },
  { id: "drafts",  label: "Drafts",  icon: PenLine,  count: 3 },
  { id: "sent",    label: "Sent",    icon: Send },
  { id: "starred", label: "Starred", icon: Star },
  { id: "archive", label: "Archive", icon: Archive },
  { id: "trash",   label: "Trash",   icon: Trash2 },
];

type TriageKind = "lead" | "newsletter" | "calendar" | "billing" | "invite";

type Triage = {
  kind: TriageKind;
  label: string;
};

type Thread = {
  id: string;
  initials: string;
  from: string;
  subject: string;
  snippet: string;
  time: string;
  unread?: boolean;
  triage?: Triage;
  selected?: boolean;
  arriving?: boolean;
};

const TRIAGE_LABELS: Record<TriageKind, string> = {
  lead:       "Lead · respond today",
  newsletter: "Newsletter · summarise weekly",
  calendar:   "Calendar · auto-accept",
  billing:    "Billing · pay end of month",
  invite:     "Invite · accept + add",
};

const THREADS_BASE: Thread[] = [
  {
    id: "t1",
    initials: "MI",
    from: "Maya Iqbal",
    subject: "Q3 partnership terms — final pass",
    snippet: "Two open flags on §4.2 — the IP carve-out language and the renewal cadence. Otherwise we're aligned and want to wrap before…",
    time: "11:42",
    unread: true,
    triage: { kind: "lead", label: TRIAGE_LABELS.lead },
    selected: true,
  },
  {
    id: "t2",
    initials: "NV",
    from: "Northvine Weekly",
    subject: "What landed this week in AI infra",
    snippet: "Inference price floors moved again, a major reranker model went open-weight, and a quiet announcement from a hyperscaler buried…",
    time: "10:18",
    triage: { kind: "newsletter", label: TRIAGE_LABELS.newsletter },
  },
  {
    id: "t3",
    initials: "CL",
    from: "Calendly",
    subject: "Tomás Reyes — 30 min scheduled · Thu 2pm",
    snippet: "Tomás has booked a slot for Thursday at 2:00 PM. The meeting will be at coopcred.example HQ; agenda attached in the invite.",
    time: "09:54",
    triage: { kind: "calendar", label: TRIAGE_LABELS.calendar },
  },
  {
    id: "t4",
    initials: "ST",
    from: "Stripe",
    subject: "Invoice #INV-2026-04 · $1,840.00",
    snippet: "Your invoice for April is available. Auto-pay is scheduled for the 30th — no action required unless you'd like to review charges.",
    time: "09:31",
    triage: { kind: "billing", label: TRIAGE_LABELS.billing },
  },
  {
    id: "t5",
    initials: "LH",
    from: "Mom",
    subject: "Sunday — bringing the lasagna or the salad?",
    snippet: "Either is fine, just let me know by Friday so I can plan around dad's roast. Also, do you eat fennel now? I genuinely cannot…",
    time: "Tue",
  },
];

// The new invite that arrives mid-script.
const INVITE_THREAD: Thread = {
  id: "t-invite",
  initials: "PD",
  from: "Priya Desai",
  subject: "Invite · Design review · Fri 3pm",
  snippet: "Calendar invite for a 45-min design review on Friday 3:00 PM. Agenda: navigation IA, empty-states, error pages.",
  time: "now",
  unread: true,
  triage: { kind: "invite", label: TRIAGE_LABELS.invite },
  arriving: true,
};

const DRAFT_MAYA =
  "Hi Maya,\n\nThanks for the markup. On §4.2 — happy to accept your IP carve-out language as drafted. On renewal cadence, I'd like to propose 12 months with a 60-day notice window rather than the rolling 30.\n\nIf that works I can have a clean redline back by EOD.";

const DRAFT_INVITE =
  "Hi Priya,\n\nAccepting — Friday 3pm works on my end. I've added the block to my calendar and will come prepped on the IA and empty-state directions.\n\nSee you then.";

/* ------------------------------------------------------------------ */
/* Calendar data                                                       */
/* ------------------------------------------------------------------ */

type CalEvent = {
  id: string;
  day: number;     // 0..4 (Mon..Fri)
  startRow: number; // 30-min slots from 8am (row 0)
  span: number;
  title: string;
  hint: string;
  tone: "slate" | "moss" | "amber" | "lilac" | "stone";
  highlight?: boolean;
};

const BASE_EVENTS: CalEvent[] = [
  { id: "e1", day: 0, startRow: 2, span: 2, title: "Standup",            hint: "Eng",         tone: "slate" },
  { id: "e2", day: 0, startRow: 6, span: 3, title: "Maya · contract",    hint: "Partnership", tone: "moss" },
  { id: "e3", day: 1, startRow: 4, span: 4, title: "Deep work · IA",     hint: "Solo",        tone: "stone" },
  { id: "e4", day: 2, startRow: 1, span: 2, title: "Standup",            hint: "Eng",         tone: "slate" },
  { id: "e5", day: 2, startRow: 8, span: 3, title: "Tomás · 1:1",        hint: "External",    tone: "amber" },
  { id: "e6", day: 3, startRow: 5, span: 4, title: "Pricing review",     hint: "All hands",   tone: "lilac" },
];

const ACCEPTED_EVENT: CalEvent = {
  id: "e-accept",
  day: 4,
  startRow: 7,
  span: 3,
  title: "Design review",
  hint: "Drafted by Mailer · auto-accept",
  tone: "moss",
  highlight: true,
};

const TONE_CLASSES: Record<CalEvent["tone"], string> = {
  slate:  "bg-foreground/[0.08] border-foreground/15 text-foreground/80",
  moss:   "bg-foreground/[0.10] border-foreground/20 text-foreground/85",
  amber:  "bg-foreground/[0.06] border-foreground/15 text-foreground/75",
  lilac:  "bg-foreground/[0.07] border-foreground/15 text-foreground/75",
  stone:  "bg-foreground/[0.04] border-foreground/12 text-foreground/70",
};

const HOURS = ["8 AM", "9", "10", "11", "12 PM", "1", "2", "3", "4", "5"];
const DAYS = ["Mon 4", "Tue 5", "Wed 6", "Thu 7", "Fri 8"];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

type Phase =
  | "mail-draft-1"      // Mailer typing reply to Maya
  | "invite-arrives"    // new invite slides into inbox
  | "mail-draft-2"      // Mailer drafting "accept + add" reply
  | "calendar-add"      // flip to calendar; accepted event slides in
  | "calendar-dwell";   // hold on calendar a moment, then loop

// First-paint: the Mailer draft pane shows the first ~half of the reply to
// Maya already typed (with a typing caret), not an empty draft. The 3-thread
// inbox + reading pane are already populated from the static THREADS_BASE.
const INITIAL_TYPED = Math.floor(DRAFT_MAYA.length * 0.45);

export function MailMock() {
  const [phase, setPhase] = useState<Phase>("mail-draft-1");
  const [tab, setTab] = useState<"mail" | "calendar">("mail");
  const [typed, setTyped] = useState(INITIAL_TYPED);
  const [showDraftBadge, setShowDraftBadge] = useState(true);
  const [inviteIn, setInviteIn] = useState(false);
  const [acceptedEventIn, setAcceptedEventIn] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  // Which draft text is currently active.
  const activeDraft = phase === "mail-draft-2" ? DRAFT_INVITE : DRAFT_MAYA;
  // Which thread is the active selection.
  const selectedId = phase === "mail-draft-1" ? "t1" : inviteIn ? "t-invite" : "t1";
  // Compose the thread list with the invite injected when due.
  const threads: Thread[] = inviteIn
    ? [INVITE_THREAD, ...THREADS_BASE.map((t) => ({ ...t, selected: false }))]
    : THREADS_BASE;
  const threadsWithSelection = threads.map((t) => ({ ...t, selected: t.id === selectedId }));

  // Draft typing — restart whenever phase swaps between draft-1 and draft-2.
  // On the very first paint we leave the partial-fill `typed` value alone so
  // the draft pane is never empty; subsequent phase changes restart from 0.
  const draftEffectRanOnce = useRef(false);
  useEffect(() => {
    if (phase !== "mail-draft-1" && phase !== "mail-draft-2") return;
    setShowDraftBadge(true);
    const CHUNK = 4;
    const total = activeDraft.length;
    let i: number;
    if (!draftEffectRanOnce.current) {
      // First run (initial paint + every loop reset): start from the
      // partial-fill seed so the draft pane is never empty.
      i = Math.min(total, INITIAL_TYPED);
      setTyped(i);
      draftEffectRanOnce.current = true;
    } else {
      // Subsequent runs within the same cycle (e.g. draft-2 takes over after
      // the invite arrives): restart from empty as the original behaviour
      // intended — draft-2 is a different message, doesn't share the seed.
      i = 0;
      setTyped(0);
    }
    const tick = () => {
      i = Math.min(total, i + CHUNK);
      setTyped(i);
      if (i < total) {
        const id = window.setTimeout(tick, 32);
        timers.current.push(id);
      }
    };
    const startId = window.setTimeout(tick, 600);
    timers.current.push(startId);
  }, [phase, activeDraft]);

  // Master scripted loop, armed on first viewport entry.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((id) => clearTimeout(id));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const runCycle = () => {
      // Reset to the partial-fill start state — same snapshot as first paint
      // so the loop never shows an empty draft pane.
      draftEffectRanOnce.current = false;
      setTab("mail");
      setPhase("mail-draft-1");
      setInviteIn(false);
      setAcceptedEventIn(false);

      // 1. Mailer drafts reply to Maya (the effect above streams the text).
      // 2. After ~5s, a new invite arrives.
      schedule(() => {
        setInviteIn(true);
        setPhase("invite-arrives");
      }, 5200);
      // 3. After invite settles, switch the draft to the "accept + add" reply.
      schedule(() => {
        setPhase("mail-draft-2");
      }, 6800);
      // 4. After draft-2 finishes, flip to calendar and slide block in.
      schedule(() => {
        setTab("calendar");
        setPhase("calendar-add");
        schedule(() => setAcceptedEventIn(true), 500);
      }, 11400);
      // 5. Dwell on calendar, then restart.
      schedule(() => setPhase("calendar-dwell"), 13200);
      schedule(() => {
        clearTimers();
        runCycle();
      }, 16800);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(runCycle, 300);
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, []);

  const draftSlice = activeDraft.slice(0, typed);
  const draftComplete = typed >= activeDraft.length;

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            mail.askfinz.ai · preview
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                ((phase === "mail-draft-1" || phase === "mail-draft-2") && !draftComplete
                  ? "bg-[hsl(var(--accent))] animate-pulse"
                  : "bg-foreground/30")
              }
            />
            {(phase === "mail-draft-1" || phase === "mail-draft-2") && !draftComplete
              ? "Drafting"
              : phase === "calendar-add" || phase === "calendar-dwell"
              ? "Calendar"
              : "Ready"}
          </span>
        </div>

        {/* Tab strip — Mail / Calendar */}
        <div className="flex items-center border-b hairline bg-foreground/[0.02] px-2">
          <TabButton
            active={tab === "mail"}
            icon={MailIcon}
            label="Mail"
            onClick={() => setTab("mail")}
          />
          <TabButton
            active={tab === "calendar"}
            icon={CalendarDays}
            label="Calendar"
            onClick={() => setTab("calendar")}
          />
          <div className="ml-auto pr-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
            {tab === "mail" ? `${threadsWithSelection.length} threads` : "Wk · May 4 – 8"}
          </div>
        </div>

        {tab === "mail" ? (
          <div className="grid grid-cols-12 min-h-[520px]">
            {/* Folders */}
            <div className="col-span-3 md:col-span-2 border-r hairline bg-foreground/[0.02] p-2 text-[12px]">
              <div className="px-2 pb-2 mb-1 border-b hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                Mail
              </div>
              <div className="space-y-0.5">
                {FOLDERS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div
                      key={f.id}
                      className={
                        "flex items-center gap-2 px-2 py-1.5 rounded transition " +
                        (f.active
                          ? "bg-foreground text-background"
                          : "text-foreground/70 hover:bg-foreground/5")
                      }
                    >
                      <Icon className="h-3 w-3 opacity-80 shrink-0" />
                      <span className="truncate text-[11px]">{f.label}</span>
                      {f.count !== undefined && (
                        <span
                          className={
                            "ml-auto text-[10px] font-mono " +
                            (f.active ? "text-background/65" : "text-foreground/65")
                          }
                        >
                          {f.count}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Message list */}
            <div className="col-span-9 md:col-span-4 border-r hairline overflow-hidden">
              <div className="px-3 py-2 border-b hairline flex items-center gap-2">
                <Inbox className="h-3.5 w-3.5 text-foreground/65" />
                <div className="text-[12px] font-medium">Inbox</div>
                <div className="ml-auto text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                  {threadsWithSelection.length} threads
                </div>
              </div>
              <div>
                {threadsWithSelection.map((t) => (
                  <div
                    key={t.id}
                    className={
                      "px-3 py-2.5 border-b hairline last:border-b-0 transition " +
                      (t.selected ? "bg-foreground/[0.04]" : "hover:bg-foreground/[0.02]") +
                      (t.arriving ? " animate-[blur-fade-in_0.5s_ease-out]" : "")
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-foreground/[0.08] text-[10px] font-medium text-foreground/75 shrink-0">
                        {t.initials}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={"text-[12px] truncate " + (t.unread ? "font-semibold text-foreground/90" : "text-foreground/70")}>
                            {t.from}
                          </span>
                          <span className="ml-auto text-[10px] font-mono text-foreground/65 shrink-0">{t.time}</span>
                        </div>
                        <div className={"text-[12px] truncate mt-0.5 " + (t.unread ? "text-foreground/85" : "text-foreground/65")}>
                          {t.subject}
                        </div>
                        <div className="text-[11px] text-foreground/65 truncate mt-0.5 leading-snug">
                          {t.snippet}
                        </div>
                        {t.triage && (
                          <div className="mt-1.5">
                            <TriageChip kind={t.triage.kind} label={t.triage.label} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reading pane + draft */}
            <div className="hidden md:flex md:col-span-6 flex-col">
              <div className="px-4 py-3 border-b hairline">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-foreground/[0.08] text-[11px] font-medium text-foreground/75">
                    {phase === "mail-draft-2" ? "PD" : "MI"}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium truncate">
                      {phase === "mail-draft-2" ? "Priya Desai" : "Maya Iqbal"}
                    </div>
                    <div className="text-[10px] font-mono text-foreground/65 truncate">
                      {phase === "mail-draft-2"
                        ? "priya@coopcred.example · just now"
                        : "maya@northvine.example · 11:42"}
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-1 text-foreground/65">
                    <button type="button" className="p-1.5 rounded hover:bg-foreground/5" aria-label="Star">
                      <Star className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" className="p-1.5 rounded hover:bg-foreground/5" aria-label="Archive">
                      <Archive className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" className="p-1.5 rounded hover:bg-foreground/5" aria-label="Reply">
                      <Reply className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="text-[14px] font-medium text-foreground/90 leading-snug">
                  {phase === "mail-draft-2"
                    ? "Invite · Design review · Fri 3pm"
                    : "Q3 partnership terms — final pass"}
                </div>
              </div>

              <div className="px-4 py-3 text-[12px] text-foreground/80 leading-relaxed border-b hairline">
                {phase === "mail-draft-2" ? (
                  <>
                    <p>Hi —</p>
                    <p className="mt-2">
                      Sending a calendar invite for our design review.
                      Friday 3:00 PM, 45 minutes. Agenda: navigation IA,
                      empty-states, error pages.
                    </p>
                    <p className="mt-2 text-foreground/65 inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3 w-3" /> design-review.ics · 1.2 KB
                    </p>
                  </>
                ) : (
                  <>
                    <p>Hi —</p>
                    <p className="mt-2">
                      I&#39;ve pushed the latest red-line to the shared folder.
                      Two open flags on §4.2: the IP carve-out language and the
                      renewal cadence. Everything else reads cleanly.
                    </p>
                    <p className="mt-2">
                      Could we wrap by Friday? Legal on our end wants the
                      signed PDF before quarter close.
                    </p>
                    <p className="mt-2 text-foreground/65 inline-flex items-center gap-1.5">
                      <Paperclip className="h-3 w-3" /> partnership-v8.pdf · 412 KB
                    </p>
                  </>
                )}
              </div>

              {/* Agent draft pane */}
              <div className="flex-1 flex flex-col bg-foreground/[0.015] min-h-[180px]">
                <div className="px-4 py-2 border-b hairline flex items-center gap-2">
                  <Bot className="h-3 w-3 text-foreground/65" />
                  <span className="text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                    {phase === "mail-draft-2"
                      ? "Drafted by Mailer · accept + add to calendar"
                      : "Drafted by Mailer · review before sending"}
                  </span>
                  {showDraftBadge && !draftComplete && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-foreground/65">
                      <Sparkles className="h-2.5 w-2.5" />
                      typing…
                    </span>
                  )}
                  {draftComplete && (
                    <span className="ml-auto text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                      ready
                    </span>
                  )}
                </div>

                <div className="px-4 py-3 flex-1 text-[12px] text-foreground/85 leading-relaxed font-sans whitespace-pre-wrap min-h-[120px]">
                  {draftSlice}
                  {showDraftBadge && !draftComplete && (
                    <span className="inline-block h-3.5 w-1.5 ml-0.5 bg-foreground/55 align-middle animate-pulse" />
                  )}
                </div>

                <div className="px-4 py-2 border-t hairline flex items-center gap-2 bg-background">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-md border hairline px-2.5 py-1 text-[11px] text-foreground/75 hover:bg-foreground/5 transition"
                  >
                    <CornerUpLeft className="h-3 w-3" /> Revise
                  </button>
                  <button
                    type="button"
                    disabled={!draftComplete}
                    className={
                      "ml-auto inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] transition " +
                      (draftComplete
                        ? "bg-foreground text-background hover:opacity-90"
                        : "bg-foreground/15 text-foreground/65 cursor-not-allowed")
                    }
                  >
                    {phase === "mail-draft-2" ? (
                      <>
                        <CalendarDays className="h-3 w-3" /> Accept + add
                      </>
                    ) : (
                      <>
                        <Send className="h-3 w-3" /> Send
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <CalendarView acceptedEventIn={acceptedEventIn} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                       */
/* ------------------------------------------------------------------ */

function TabButton({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: typeof MailIcon;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 px-3 py-2 text-[11.5px] border-b-2 transition " +
        (active
          ? "border-foreground text-foreground"
          : "border-transparent text-foreground/65 hover:text-foreground/85")
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

function TriageChip({ kind, label }: { kind: TriageKind; label: string }) {
  void kind;
  return (
    <span className="inline-flex items-center gap-1 rounded-md border hairline px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] text-foreground/65 bg-background">
      <Sparkles className="h-2.5 w-2.5 text-foreground/65" />
      {label}
    </span>
  );
}

function CalendarView({ acceptedEventIn }: { acceptedEventIn: boolean }) {
  // 30-minute slots from 8 AM to 6 PM = 20 rows.
  const ROWS = 20;
  const events = acceptedEventIn ? [...BASE_EVENTS, ACCEPTED_EVENT] : BASE_EVENTS;

  return (
    <div className="min-h-[520px] grid grid-cols-12">
      {/* Time gutter + grid */}
      <div className="col-span-12 p-4 bg-background">
        {/* Day header */}
        <div className="grid grid-cols-[56px_repeat(5,1fr)] gap-px bg-foreground/10 rounded-t-md overflow-hidden">
          <div className="bg-background p-2 text-[10px] tracking-[0.14em] uppercase text-foreground/65 font-mono">
            wk 19
          </div>
          {DAYS.map((d, i) => (
            <div
              key={d}
              className={
                "bg-background p-2 text-[11px] font-medium tracking-tight text-foreground/75 " +
                (i === 4 ? "" : "")
              }
            >
              {d}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="relative grid grid-cols-[56px_repeat(5,1fr)] gap-px bg-foreground/10 rounded-b-md overflow-hidden">
          {/* Time column */}
          <div className="bg-background">
            {HOURS.map((h) => (
              <div
                key={h}
                className="h-10 border-b hairline px-2 py-1 text-[10px] font-mono text-foreground/65 leading-none flex items-start"
              >
                {h}
              </div>
            ))}
          </div>
          {/* Day columns */}
          {Array.from({ length: 5 }).map((_, dayIdx) => (
            <div key={dayIdx} className="bg-background relative">
              {/* Hour grid lines */}
              {HOURS.map((_, hi) => (
                <div key={hi} className="h-10 border-b hairline" />
              ))}
              {/* Events for this day */}
              {events
                .filter((e) => e.day === dayIdx)
                .map((e) => {
                  const top = (e.startRow * 20) + 2; // 20px per 30-min row
                  const height = Math.max(28, e.span * 20 - 4);
                  const base = TONE_CLASSES[e.tone];
                  return (
                    <div
                      key={e.id}
                      className={
                        "absolute left-1 right-1 rounded-md border px-2 py-1.5 text-[10.5px] leading-tight overflow-hidden " +
                        base +
                        (e.highlight
                          ? " animate-[blur-fade-in_0.6s_ease-out] ring-2 ring-[hsl(var(--accent))]/40 ring-offset-1 ring-offset-background"
                          : "")
                      }
                      style={{ top, height }}
                    >
                      <div className="flex items-center gap-1">
                        {e.highlight && (
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))]" />
                        )}
                        <span className="font-medium truncate">{e.title}</span>
                      </div>
                      <div
                        className={
                          "mt-0.5 truncate font-mono text-[9.5px] " +
                          (e.highlight ? "text-foreground/70" : "text-foreground/65")
                        }
                      >
                        {e.hint}
                      </div>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>

        {/* Footer caption */}
        <div className="mt-3 flex items-center gap-3 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
          {acceptedEventIn ? (
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3" />
              Added · Friday 3:00 PM · 45 min
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              Week of May 4 — 6 events scheduled
            </span>
          )}
          <span className="ml-auto text-foreground/65">Mailer auto-accepts when the invite matches a triage rule</span>
        </div>
      </div>
    </div>
  );
}
